import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Button,
  Typography,
  IconButton,
  Fade,
  Popper,
  LinearProgress,
  Portal,
  Tooltip,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Badge,
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowForward as NextIcon,
  ArrowBack as BackIcon,
  SkipNext as SkipIcon,
  HelpOutline as HelpIcon,
  School as TourIcon,
  PlayArrow as PlayIcon,
  Replay as RestartIcon,
} from '@mui/icons-material';
import type { ComponentTour, ComponentTourStep } from './ComponentTours';
import { getComponentTourByRoute } from './ComponentTours';

interface ComponentTourContextType {
  startTour: (tourId: string) => void;
  endTour: () => void;
  isActive: boolean;
  currentTour: ComponentTour | null;
  availableTours: ComponentTour[];
}

const ComponentTourContext = createContext<ComponentTourContextType | undefined>(undefined);

export const useComponentTour = () => {
  const context = useContext(ComponentTourContext);
  if (!context) {
    throw new Error('useComponentTour must be used within ComponentTourProvider');
  }
  return context;
};

interface ComponentTourProviderProps {
  children: React.ReactNode;
}

export const ComponentTourProvider: React.FC<ComponentTourProviderProps> = ({ children }) => {
  const location = useLocation();
  const [isActive, setIsActive] = useState(false);
  const [currentTour, setCurrentTour] = useState<ComponentTour | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const [showHelpButton, setShowHelpButton] = useState(true);
  const popperRef = useRef<HTMLDivElement>(null);

  // Get available tours for current route
  const availableTours = React.useMemo(() => {
    const tour = getComponentTourByRoute(location.pathname);
    return tour ? [tour] : [];
  }, [location.pathname]);

  // Check if component tour has been seen
  useEffect(() => {
    const seenTours = JSON.parse(localStorage.getItem('seenComponentTours') || '{}');
    const currentRouteTour = getComponentTourByRoute(location.pathname);
    
    if (currentRouteTour && !seenTours[currentRouteTour.id]) {
      // Show help button with badge for new tours
      setShowHelpButton(true);
    }
  }, [location.pathname]);

  // Find target element
  useEffect(() => {
    if (isActive && currentTour && currentTour.steps[currentStep]) {
      const step = currentTour.steps[currentStep];
      
      const findElement = () => {
        let element: HTMLElement | null = null;
        
        if (step.target === 'body') {
          element = document.body;
        } else {
          // Try multiple strategies to find element
          element = document.querySelector(step.target);
          
          // If not found, try with data-tour attribute
          if (!element) {
            element = document.querySelector(`[data-tour="${step.target}"]`);
          }
          
          // If still not found, try by aria-label
          if (!element) {
            element = document.querySelector(`[aria-label="${step.target}"]`);
          }
        }
        
        // Only set target if element exists and is properly rendered
        if (element && element.offsetParent !== null) {
          setTargetElement(element);
        } else {
          setTargetElement(null);
        }
      };

      // Try to find element immediately and after delays
      findElement();
      const timers = [
        setTimeout(findElement, 100),
        setTimeout(findElement, 500),
        setTimeout(findElement, 1000),
      ];
      
      return () => timers.forEach(timer => clearTimeout(timer));
    }
  }, [isActive, currentTour, currentStep]);

  const startTour = (tourId: string) => {
    const tour = availableTours.find(t => t.id === tourId);
    if (tour) {
      setCurrentTour(tour);
      setCurrentStep(0);
      setIsActive(true);
      
      // Mark tour as seen
      const seenTours = JSON.parse(localStorage.getItem('seenComponentTours') || '{}');
      seenTours[tourId] = true;
      localStorage.setItem('seenComponentTours', JSON.stringify(seenTours));
    }
  };

  const endTour = () => {
    setIsActive(false);
    setCurrentTour(null);
    setCurrentStep(0);
  };

  const nextStep = () => {
    if (currentTour && currentStep < currentTour.steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      endTour();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const getCurrentStep = (): ComponentTourStep | null => {
    if (!currentTour || !currentTour.steps[currentStep]) return null;
    return currentTour.steps[currentStep];
  };

  const currentStepData = getCurrentStep();
  const shouldShowOverlay = isActive && currentStepData?.target !== 'body';

  const contextValue: ComponentTourContextType = {
    startTour,
    endTour,
    isActive,
    currentTour,
    availableTours,
  };

  return (
    <ComponentTourContext.Provider value={contextValue}>
      {children}

      {/* Tour Overlay */}
      <Portal>
        {shouldShowOverlay && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1400,
              pointerEvents: isActive ? 'auto' : 'none',
            }}
          >
            {/* Backdrop with spotlight */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: 'rgba(0, 0, 0, 0.7)',
                pointerEvents: 'auto',
              }}
              onClick={endTour}
            />
            
            {/* Spotlight */}
            {targetElement && currentStepData && (
              <Box
                sx={{
                  position: 'absolute',
                  ...(() => {
                    try {
                      const rect = targetElement.getBoundingClientRect();
                      if (rect.width === 0 || rect.height === 0) {
                        return { display: 'none' };
                      }
                      const padding = currentStepData.highlightPadding || 8;
                      return {
                        top: rect.top - padding,
                        left: rect.left - padding,
                        width: rect.width + (padding * 2),
                        height: rect.height + (padding * 2),
                      };
                    } catch (error) {
                      console.warn('Error positioning tour spotlight:', error);
                      return { display: 'none' };
                    }
                  })(),
                  boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.7)',
                  borderRadius: 1,
                  pointerEvents: 'none',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: -2,
                    left: -2,
                    right: -2,
                    bottom: -2,
                    border: '2px solid',
                    borderColor: 'primary.main',
                    borderRadius: 1,
                    animation: 'pulse 2s infinite',
                  },
                }}
              />
            )}
          </Box>
        )}

        {/* Tour Content */}
        {isActive && currentStepData && (
          currentStepData.target === 'body' ? (
            // Center placement
            <Fade in={isActive}>
              <Box
                sx={{
                  position: 'fixed',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 1500,
                }}
              >
                <Paper
                  elevation={8}
                  sx={{
                    p: 3,
                    maxWidth: 500,
                    width: '90vw',
                  }}
                >
                  <TourStepContent
                    tour={currentTour!}
                    step={currentStepData}
                    currentIndex={currentStep}
                    totalSteps={currentTour!.steps.length}
                    onNext={nextStep}
                    onPrev={prevStep}
                    onEnd={endTour}
                    isFirst={currentStep === 0}
                    isLast={currentStep === currentTour!.steps.length - 1}
                  />
                </Paper>
              </Box>
            </Fade>
          ) : (
            // Popper placement
            targetElement && (() => {
              try {
                const rect = targetElement.getBoundingClientRect();
                if (rect.width === 0 || rect.height === 0) {
                  return null;
                }
                return (
                  <Popper
                    open={true}
                    anchorEl={targetElement}
                    placement={currentStepData.placement as any || 'bottom'}
                    ref={popperRef}
                    sx={{ zIndex: 1500 }}
                    modifiers={[
                      {
                        name: 'offset',
                        options: {
                          offset: [0, 16],
                        },
                      },
                      {
                        name: 'preventOverflow',
                        options: {
                          boundary: 'viewport',
                          padding: 16,
                        },
                      },
                      {
                        name: 'flip',
                        enabled: true,
                      },
                    ]}
                  >
                    <Paper
                      elevation={8}
                      sx={{
                        p: 3,
                        maxWidth: 400,
                        width: '90vw',
                      }}
                    >
                      <TourStepContent
                        tour={currentTour!}
                        step={currentStepData}
                        currentIndex={currentStep}
                        totalSteps={currentTour!.steps.length}
                        onNext={nextStep}
                        onPrev={prevStep}
                        onEnd={endTour}
                        isFirst={currentStep === 0}
                        isLast={currentStep === currentTour!.steps.length - 1}
                      />
                    </Paper>
                  </Popper>
                );
              } catch (error) {
                console.warn('Error rendering tour popper:', error);
                return null;
              }
            })()
          )
        )}
      </Portal>

      {/* Help Button / Speed Dial */}
      {showHelpButton && availableTours.length > 0 && (
        <Portal>
          <Box
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 1300,
            }}
          >
            <SpeedDial
              ariaLabel="Help and Tours"
              icon={
                <SpeedDialIcon
                  icon={
                    <Badge
                      color="secondary"
                      variant="dot"
                      invisible={(() => {
                        const seenTours = JSON.parse(localStorage.getItem('seenComponentTours') || '{}');
                        return availableTours.every(tour => seenTours[tour.id]);
                      })()}
                    >
                      <HelpIcon />
                    </Badge>
                  }
                />
              }
              direction="up"
            >
              {availableTours.map((tour) => {
                const seenTours = JSON.parse(localStorage.getItem('seenComponentTours') || '{}');
                const isNew = !seenTours[tour.id];
                
                return (
                  <SpeedDialAction
                    key={tour.id}
                    icon={
                      <Badge color="secondary" variant="dot" invisible={!isNew}>
                        <TourIcon />
                      </Badge>
                    }
                    tooltipTitle={
                      <Box>
                        <Typography variant="subtitle2">{tour.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {tour.description}
                        </Typography>
                        {isNew && (
                          <Typography variant="caption" color="secondary" display="block">
                            New!
                          </Typography>
                        )}
                      </Box>
                    }
                    onClick={() => startTour(tour.id)}
                  />
                );
              })}
            </SpeedDial>
          </Box>
        </Portal>
      )}

      <style>
        {`
          @keyframes pulse {
            0% {
              opacity: 1;
              transform: scale(1);
            }
            50% {
              opacity: 0.5;
              transform: scale(1.05);
            }
            100% {
              opacity: 1;
              transform: scale(1);
            }
          }
        `}
      </style>
    </ComponentTourContext.Provider>
  );
};

// Tour Step Content Component
interface TourStepContentProps {
  tour: ComponentTour;
  step: ComponentTourStep;
  currentIndex: number;
  totalSteps: number;
  onNext: () => void;
  onPrev: () => void;
  onEnd: () => void;
  isFirst: boolean;
  isLast: boolean;
}

const TourStepContent: React.FC<TourStepContentProps> = ({
  tour,
  step,
  currentIndex,
  totalSteps,
  onNext,
  onPrev,
  onEnd,
  isFirst,
  isLast,
}) => {
  return (
    <>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="overline" color="text.secondary">
            {tour.name}
          </Typography>
          <Typography variant="caption" display="block" color="text.secondary">
            Step {currentIndex + 1} of {totalSteps}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onEnd}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Progress */}
      <LinearProgress
        variant="determinate"
        value={(currentIndex + 1) / totalSteps * 100}
        sx={{ height: 4, borderRadius: 2, mb: 3 }}
      />

      {/* Content */}
      {step.title && (
        <Typography variant="h6" gutterBottom>
          {step.title}
        </Typography>
      )}
      
      <Box sx={{ mb: 3 }}>
        {typeof step.content === 'string' ? (
          <Typography variant="body2" color="text.secondary">
            {step.content}
          </Typography>
        ) : (
          step.content
        )}
      </Box>

      {/* Actions */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Button
          size="small"
          onClick={onEnd}
          startIcon={<SkipIcon />}
        >
          End Tour
        </Button>
        
        <Box display="flex" gap={1}>
          <Button
            size="small"
            onClick={onPrev}
            disabled={isFirst}
            startIcon={<BackIcon />}
          >
            Back
          </Button>
          
          <Button
            size="small"
            variant="contained"
            onClick={onNext}
            endIcon={!isLast && <NextIcon />}
          >
            {isLast ? 'Finish' : 'Next'}
          </Button>
        </Box>
      </Box>
    </>
  );
};